import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIScrollableContainer } from "../../common_ui_elements/ui_scrollable_container.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { UIAvatarSelectionItem } from "../../common_ui_elements/ui_avatar_selection_item.js";
import { TelegramAvatarIdentity } from "../../../common/telegram_avatar.js";
import { claimHeartGifts, dismissReminder, remindFriend, sendHeart } from "../../../api/requests/friends.requests.js";
import { TelegramAPISettings } from "../../../telegram_api_settings.js";
import { FriendsCache } from "../../../friends/friends_cache.js";
import { InviteService } from "../../../friends/invite_service.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import type { RewardFlySource } from "../../reward_fly/reward_fly_to_hud.js";
import type { ClaimHeartGiftsDto, FriendDto, FriendProfileDto, FriendReminderDto, FriendsListDto, GiftRewardDto, HeartGiftDto } from "../../../api/dto/friends.dto.js";
import { adoptEconomyFromPlayer } from "../../../api/player_sync.js";
import { ApiError } from "../../../api/http_client.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { DialogManager } from "../../dialog/dialog_manager.js";
import { InviteFriendBlock } from "../../common_ui_elements/invite_friend_block.js";
import { NotificationBadge } from "../../common_ui_elements/notification_badge.js";
import { OverlayManager } from "../../overlay/overlay_manager.js";
import { OverlayFactory } from "../../overlay/overlay_factory.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import { SafeAreas } from "../../safe_areas.js";
import { Spinner } from "../leaderboard/spinner.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";

const HEADER_H = 442;
const HEADER_TOP_GAP = -80;
const HEADER_TABS_H = 120;
const HEADER_ICON_LEFT = 80;

const TAB_W = 513;
const TAB_H = 148;
const TABS_BOTTOM_GAP = 34;
const TAB_LABEL_Y = 46;
const TAB_LABEL_H = 60;
const RECEIVE_TAB = 1;
const RECEIVE_BADGE_W = 70;
const RECEIVE_BADGE_H = 70;
const RECEIVE_BADGE_OFFSET_X = -4;
const RECEIVE_BADGE_OFFSET_Y = 1;

const CONTENT_GAP = 50;

const CARD_W = 1024;
const CARD_H = 150;
const CARD_GAP = 25;
const CARD_ACTION_RIGHT_GAP = 2;

const NAV_BAR_H = 370;
const INVITE_W = 987;
const INVITE_H = 294;

const SPINNER_SIZE = 120;

const GIFT_FLY_MAX_HEARTS = 5;

const FRIENDS_POLL_MS = 10_000;

const STATUS_NAME_Y = 15;
const STATUS_Y = 80;
const STATUS_H = 46;
const STATUS_FONT = 34;
const REMIND_STATUS_X = 25;
const REMIND_STATUS_W = 540;
const REMIND_BADGE_SIZE = 56;
const REMIND_BADGE_X = 6;
const REMIND_BADGE_Y = 2;
const REMIND_BADGE_HIT_SIZE = 110;

interface FriendCardData
{
  profile: FriendProfileDto;
  can_send: boolean;
  next_send_at: number | null;
  gift_id: string | null;
  status_key: string | null;
  reminder: FriendReminderDto | null;
}

interface GiftRewardIcons
{
  coin: UIImage;
  heart: UIImage;
}

export class FriendsScreen extends UIElement
{
  private events_controller: BasicEventsController | null = null;

  private active_tab = 0;

  private readonly tab_backgrounds: UIImage[] = [];
  private readonly tab_contents: UIScrollableContainer[] = [];
  private readonly gift_timers: { text: UITextElement; until: number; shown: string }[] = [];

  private friends: FriendDto[] = [];
  private gifts: HeartGiftDto[] = [];
  private reminders: FriendReminderDto[] = [];
  private gift_reward: GiftRewardDto = { hearts: 1, coins: 100 };
  private is_destroyed = false;
  private is_loaded = false;
  private invite_block: InviteFriendBlock | null = null;
  private loading_spinner: Spinner | null = null;
  private receive_tab: UIElementHolder | null = null;
  private receive_badge: NotificationBadge | null = null;
  private last_poll_at = Date.now();
  private is_polling = false;
  private readonly on_visibility_change = () =>
  {
    if ( document.visibilityState === "visible" )
      this.last_poll_at = 0;
  };

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number )
  {
    super( ctx, x, y, width, height, false, false );

    this.initializeEventsController( this.ctx.canvas );

    this.createScreenBackground();
    this.createHeader();
    this.createTabs();
    this.createContents();
    this.createInviteBlock();

    void this.loadFriends();
    void this.claimInviteRewards();

    document.addEventListener( "visibilitychange", this.on_visibility_change );
  }

  private pollFriends(): void
  {
    if ( this.is_destroyed || this.is_polling || Date.now() - this.last_poll_at < FRIENDS_POLL_MS )
      return;

    this.last_poll_at = Date.now();
    this.is_polling = true;

    FriendsCache.fetch()
      .then( data =>
      {
        const current: FriendsListDto = { friends: this.friends, gifts: this.gifts, reminders: this.reminders, gift_reward: this.gift_reward };
        if ( !this.is_destroyed && JSON.stringify( data ) !== JSON.stringify( current ) )
          this.applyFriends( data );
      } )
      .catch( e => console.warn( "[FriendsScreen] Failed to refresh friends", e ) )
      .finally( () => { this.is_polling = false; } );
  }

  private async claimInviteRewards(): Promise<void>
  {
    const stats_before = StatisticsStorage.load();
    const result = await InviteService.claim();
    if ( !result || this.is_destroyed || !this.invite_block )
      return;

    FriendsCache.invalidate();
    void this.loadFriends();

    const source: RewardFlySource = {
      target: "coin",
      img_id: ImgID.UI_ShopBlockImg,
      center: this.invite_block.coinsCenter(),
      size: this.invite_block.coinsSize(),
      amount: result.coins,
    };

    RewardFlyToHud.play( this.ctx, this, [ source ], stats_before );
  }

  private async loadFriends(): Promise<void>
  {
    const cached = FriendsCache.peek();
    if ( cached )
      this.applyFriends( cached );

    if ( FriendsCache.isFresh() )
      return;

    if ( !cached )
      this.showLoadingSpinner();

    try
    {
      const data = await FriendsCache.fetch();
      if ( this.is_destroyed )
        return;

      if ( !cached || JSON.stringify( data ) !== JSON.stringify( cached ) )
        this.applyFriends( data );
    }
    catch ( e )
    {
      console.warn( "[FriendsScreen] Failed to load friends", e );
    }
    finally
    {
      this.hideLoadingSpinner();
    }
  }

  private applyFriends( data: FriendsListDto ): void
  {
    this.friends = data.friends;
    this.gifts = data.gifts;
    this.reminders = data.reminders ?? [];
    this.gift_reward = data.gift_reward ?? this.gift_reward;
    this.is_loaded = true;
    this.syncReceiveBadge();
    this.rebuildContents();
  }

  private saveFriendsToCache(): void
  {
    FriendsCache.update( { friends: this.friends, gifts: this.gifts, reminders: this.reminders, gift_reward: this.gift_reward } );
  }

  private showLoadingSpinner(): void
  {
    if ( this.loading_spinner )
      return;

    const content_top = this.contentTop();
    const content_h = this.inviteBlockTop() - content_top - LayoutScaler.yFromDesign( 25 );
    const spinner_size = LayoutScaler.widthFromDesign( SPINNER_SIZE );
    const spinner_x = ( this.rect.getW() - spinner_size ) / 2;
    const spinner_y = content_top + ( content_h - spinner_size ) / 2;

    this.loading_spinner = new Spinner( this.ctx, spinner_x, spinner_y, spinner_size, spinner_size );
    this.addChild( this.loading_spinner );
  }

  private hideLoadingSpinner(): void
  {
    if ( !this.loading_spinner )
      return;

    this.removeChild( this.loading_spinner );
    this.loading_spinner = null;
  }

  private createScreenBackground(): void
  {
    const full_x = -Game.safeAreaLeft();
    const full_y = -Game.safeAreaTop();
    const full_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const full_h = this.rect.getH() + Game.safeAreaTop();

    const background = new RoundedRectangle(
      this.ctx,
      full_x,
      full_y,
      full_w,
      full_h,
      0,
      "#1A176E"
    );

    this.addChild( background );
  }

  private createHeader(): void
  {
    const header_x = -Game.safeAreaLeft();
    const header_y = LayoutScaler.yFromDesign( HEADER_TOP_GAP ) - Game.safeAreaTop();
    const header_w = this.rect.getW() + Game.safeAreaLeft() + Game.safeAreaRight();
    const header_h = LayoutScaler.heightFromDesign( HEADER_H ) + Game.safeAreaTop();

    const header = new UINineSliceElement(
      this.ctx,
      header_x,
      header_y,
      header_w,
      header_h,
      ImgID.UI_FriendsHeaderBg,
      "friends_header_tabs_bg"
    );

    this.addChild( header );

    const text_bottom = header_h - LayoutScaler.heightFromDesign( HEADER_TABS_H );
    const info_icon_size = LayoutScaler.heightFromDesign( 120 );
    const info_icon_x = Game.safeAreaLeft() + LayoutScaler.xFromDesign( HEADER_ICON_LEFT );
    const info_icon_y = text_bottom - info_icon_size - LayoutScaler.yFromDesign( 65 );
    const title_x = info_icon_x + info_icon_size + LayoutScaler.xFromDesign( 25 );
    const title_h = LayoutScaler.heightFromDesign( 150 );
    const title_w = LayoutScaler.widthFromDesign( 850 );
    const title_y = text_bottom - title_h - LayoutScaler.yFromDesign( 30 );

    const title = new UITextElement(
      this.ctx,
      title_x,
      title_y,
      title_w,
      title_h,
      {
        localization_key: "FRIENDS.DESCRIPTION",
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Top,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.friends_title_outline,
      }
    );

    const info_icon = new UIImage(
      this.ctx,
      info_icon_x,
      info_icon_y,
      info_icon_size,
      info_icon_size,
      ImgID.UI_LeaderboardInfoButton,
      {},
      true
    );

    info_icon.setOnClick( () => OverlayFactory.openOverlay( OverlayId.FRIENDS_INFO_OVERLAY ) );

    header.addChild( info_icon );
    header.addChild( title );
  }

  private createTabs(): void
  {
    const tab_w = LayoutScaler.widthFromDesign( TAB_W );
    const tab_h = LayoutScaler.heightFromDesign( TAB_H );
    const label_y = LayoutScaler.yFromDesign( TAB_LABEL_Y );
    const label_h = LayoutScaler.heightFromDesign( TAB_LABEL_H );

    for ( let i = 0; i < 2; i++ )
    {
      const tab = new UIElementHolder( this.ctx, this.tabX( i ), this.tabY(), tab_w, tab_h, false, false );
      tab.setOnClick( () => this.setActiveTab( i ) );

      const background = new UIImage( this.ctx, 0, 0, tab_w, tab_h, ImgID.UI_FriendsTab );
      background.setClickable( false );
      tab.addChild( background, true );
      this.tab_backgrounds.push( background );

      const label = new UITextElement(
        this.ctx,
        0,
        label_y,
        tab_w,
        label_h,
        {
          localization_key: i === 0 ? "FRIENDS.GIFT" : "FRIENDS.RECEIVE",
          font_size: LayoutScaler.textSizeFromDesign( 50 ),
          font_weight: 900,
          text_style: TextStyleScheme.rubik,
          text_color: ColorScheme.white,
          text_align: MyTextAlign.Center,
          vertical_align: MyTextVerticalAlign.Center,
          outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
          outline_color: ColorScheme.friends_tab_outline,
          auto_wrap: false,
          ...TextStyleScheme.buttonShadow(),
        }
      );

      label.setClickable( false );
      tab.addChild( label, true );

      this.addChild( tab );

      if ( i === RECEIVE_TAB )
        this.receive_tab = tab;
    }

    this.syncTabs();
  }

  private syncReceiveBadge(): void
  {
    const tab = this.receive_tab;
    const count = this.gifts.length;

    if ( !tab )
      return;

    if ( count === 0 )
    {
      if ( this.receive_badge )
        tab.removeChild( this.receive_badge );

      this.receive_badge = null;
      return;
    }

    if ( !this.receive_badge )
    {
      const tab_w = LayoutScaler.widthFromDesign( TAB_W );
      const badge_w = LayoutScaler.widthFromDesign( RECEIVE_BADGE_W );
      const badge_h = LayoutScaler.heightFromDesign( RECEIVE_BADGE_H );
      const x = tab_w - badge_w + LayoutScaler.xFromDesign( RECEIVE_BADGE_OFFSET_X );
      const y = LayoutScaler.yFromDesign( RECEIVE_BADGE_OFFSET_Y );

      this.receive_badge = new NotificationBadge( this.ctx, x, y, badge_w, badge_h );
      tab.addChild( this.receive_badge, true );
    }

    this.receive_badge.setCount( count );
  }

  private tabX( index: number ): number
  {
    const tab_w = LayoutScaler.widthFromDesign( TAB_W );
    const group_x = ( this.rect.getW() - tab_w * 2 ) / 2;

    return group_x + index * tab_w;
  }

  private headerBottom(): number
  {
    return LayoutScaler.yFromDesign( HEADER_TOP_GAP ) + LayoutScaler.heightFromDesign( HEADER_H );
  }

  private tabY(): number
  {
    return this.headerBottom() - LayoutScaler.heightFromDesign( TAB_H + TABS_BOTTOM_GAP );
  }

  private contentTop(): number
  {
    return this.headerBottom() + LayoutScaler.heightFromDesign( CONTENT_GAP );
  }

  private setActiveTab( index: number ): void
  {
    if ( index === this.active_tab )
      return;

    this.removeChild( this.tab_contents[this.active_tab] );
    this.active_tab = index;
    this.addChild( this.tab_contents[this.active_tab] );

    this.syncTabs();
    this.forceMakeShouldBeRendered();
  }

  private syncTabs(): void
  {
    this.tab_backgrounds.forEach( ( background, i ) =>
    {
      const is_active = i === this.active_tab;
      const is_left = i === 0;

      background.setImageId( is_active ? ImgID.UI_FriendsTabActive : ImgID.UI_FriendsTab );
      background.setFlipX( is_left !== is_active );
    } );
  }

  private createContents(): void
  {
    const content_top = this.contentTop();
    const content_h = this.inviteBlockTop() - content_top - LayoutScaler.yFromDesign( 25 );
    const content_w = this.rect.getW();

    for ( let i = 0; i < 2; i++ )
    {
      const scroll = new UIScrollableContainer(
        this.ctx,
        0,
        content_top,
        content_w,
        content_h,
        {
          direction: "vertical",
          scroll_speed: 1,
          scroll_sensitivity: 0.9,
          enable_momentum: true,
          enable_touch_scroll: true,
          enable_rubber_band: false,
        }
      );

      const is_claim_tab = i === 1;
      const cards = is_claim_tab ? this.claimTabCards() : this.giftTabCards();
      const is_empty = this.is_loaded && cards.length === 0;
      const list_h = is_empty ? content_h : this.listHeight( cards.length );

      const list = new UIElementHolder(
        this.ctx,
        0,
        0,
        content_w,
        list_h
      );

      if ( is_empty && is_claim_tab )
        this.createNoGiftsText( list, content_w, content_h );
      else if ( is_empty )
        this.createEmptyText( list, content_w, content_h );
      else
        this.createList( list, i, cards );

      scroll.addScrollableChild( list );
      scroll.setContentHeight( list_h );

      this.tab_contents.push( scroll );
    }

    this.addChild( this.tab_contents[this.active_tab] );
  }

  private rebuildContents(): void
  {
    this.removeChild( this.tab_contents[this.active_tab] );
    this.tab_contents.length = 0;
    this.gift_timers.length = 0;
    this.createContents();
  }

  private giftTabCards(): FriendCardData[]
  {
    return this.friends.map( friend => ( {
      profile: friend,
      can_send: friend.can_send_heart,
      next_send_at: friend.next_send_at ? Date.parse( friend.next_send_at ) : null,
      gift_id: null,
      status_key: null,
      reminder: null,
    } ) );
  }

  private claimTabCards(): FriendCardData[]
  {
    const gift_cards = this.gifts.map( gift => ( {
      profile: gift.from,
      can_send: false,
      next_send_at: null,
      gift_id: gift.id,
      status_key: gift.kind === "reply" ? "FRIENDS.STATUS_THANKED" : "FRIENDS.STATUS_GIFTED",
      reminder: null,
    } ) );

    const reminder_cards = this.reminders.map( reminder => ( {
      profile: reminder.friend,
      can_send: false,
      next_send_at: null,
      gift_id: null,
      status_key: "FRIENDS.STATUS_UNCLAIMED",
      reminder,
    } ) );

    return [ ...gift_cards, ...reminder_cards ];
  }

  private listHeight( card_count: number ): number
  {
    if ( card_count === 0 )
      return 0;

    const card_h = LayoutScaler.heightFromDesign( CARD_H );
    const gap = LayoutScaler.heightFromDesign( CARD_GAP );

    return card_count * card_h + ( card_count - 1 ) * gap;
  }

  private createEmptyText( parent: UIElement, content_w: number, content_h: number ): void
  {
    const text_w = LayoutScaler.widthFromDesign( 944 );
    const text_x = ( content_w - text_w ) / 2;

    const text = new UITextElement(
      this.ctx,
      text_x,
      0,
      text_w,
      content_h,
      {
        localization_key: "FRIENDS.EMPTY",
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: true,
        auto_wrap: false,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    text.setClickable( false );
    parent.addChild( text );
  }

  private createNoGiftsText( parent: UIElement, content_w: number, content_h: number ): void
  {
    const text_w = LayoutScaler.widthFromDesign( 980 );
    const text_x = ( content_w - text_w ) / 2;

    const text = new UITextElement(
      this.ctx,
      text_x,
      0,
      text_w,
      content_h,
      {
        localization_key: "FRIENDS.NO_GIFTS",
        font_size: LayoutScaler.textSizeFromDesign( 54 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    text.setClickable( false );
    parent.addChild( text );
  }

  private createList( parent: UIElement, tab_index: number, cards: FriendCardData[] ): void
  {
    const card_h = LayoutScaler.heightFromDesign( CARD_H );
    const gap = LayoutScaler.heightFromDesign( CARD_GAP );

    cards.forEach( ( data, i ) =>
    {
      const y = i * ( card_h + gap );
      this.createCard( parent, y, tab_index, data );
    } );
  }

  private friendIdentity( profile: FriendProfileDto ): TelegramAvatarIdentity
  {
    return {
      seed: profile.id,
      first_name: this.friendName( profile ),
      last_name: "",
      photo_url: profile.telegram_photo_url,
    };
  }

  private friendName( profile: FriendProfileDto ): string
  {
    return profile.nickname?.trim() || LocalizationManager.tWithParams( "FRIENDS.DEFAULT_NAME", [ String( profile.id ) ] );
  }

  private createCard( parent: UIElement, y: number, tab_index: number, data: FriendCardData ): void
  {
    const card_w = LayoutScaler.widthFromDesign( CARD_W );
    const card_h = LayoutScaler.heightFromDesign( CARD_H );
    const card_x = ( this.rect.getW() - card_w ) / 2;

    const card = new UINineSliceElement(
      this.ctx,
      card_x,
      y,
      card_w,
      card_h,
      ImgID.UI_FriendsCardBg,
      "friends_card_bg"
    );

    parent.addChild( card );

    const avatar_size = LayoutScaler.heightFromDesign( 128 );
    const avatar_x = LayoutScaler.xFromDesign( 24 );
    const avatar_y = LayoutScaler.yFromDesign( 12 );
    const avatar_border = LayoutScaler.widthFromDesign( 4 );

    const avatar = new UIAvatarSelectionItem(
      this.ctx,
      avatar_x,
      avatar_y,
      avatar_size,
      avatar_size,
      data.profile.avatar_id,
      () => {},
      avatar_border,
      0,
      undefined,
      false,
      {
        type: "linear-gradient",
        from: ColorScheme.white,
        to: ColorScheme.white,
        direction: "vertical",
      },
      data.profile.facebook_avatar_url,
      this.friendIdentity( data.profile )
    );

    avatar.setClickable( false );
    card.addChild( avatar );

    if ( data.reminder )
      this.createRemindBadge( card, data.reminder );

    const name_x = avatar_x + avatar_size + LayoutScaler.xFromDesign( 5 );
    const name_y = LayoutScaler.yFromDesign( data.status_key ? STATUS_NAME_Y : 45 );
    const name_w = LayoutScaler.widthFromDesign( 315 );
    const name_h = LayoutScaler.heightFromDesign( 60 );

    const name = new UITextElement(
      this.ctx,
      name_x,
      name_y,
      name_w,
      name_h,
      {
        text: this.friendName( data.profile ),
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.friends_card_name,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
      }
    );

    name.setClickable( false );
    card.addChild( name );

    if ( data.status_key )
      this.createStatusText( card, name_x, name_w, data );

    if ( data.reminder )
    {
      this.createRemindButton( card, card_w, data.reminder );
      return;
    }

    const coins_text = `${this.gift_reward.coins}`;
    const hearts_text = `+${this.gift_reward.hearts}`;
    const coin_icon = this.createCardCounter( card, 528, 104, ImgID.UI_ShopBlockImg, coins_text, false );
    const heart_icon = this.createCardCounter( card, 637, 109, ImgID.IconHeart, hearts_text, true );
    const reward_icons: GiftRewardIcons = { coin: coin_icon, heart: heart_icon };

    const is_gift_sent = tab_index === 0 && !data.can_send && data.next_send_at !== null;
    if ( is_gift_sent )
      this.createSentBlock( card, card_w, data.next_send_at! );
    else
      this.createCardButton( card, card_w, tab_index, data, reward_icons );
  }

  private createStatusText( card: UIElement, name_x: number, name_w: number, data: FriendCardData ): void
  {
    const is_reminder = data.reminder !== null;
    const status_x = is_reminder ? name_x + LayoutScaler.xFromDesign( REMIND_STATUS_X ) : name_x;
    const status_y = LayoutScaler.yFromDesign( STATUS_Y );
    const status_w = is_reminder ? LayoutScaler.widthFromDesign( REMIND_STATUS_W ) : name_w;
    const status_h = LayoutScaler.heightFromDesign( STATUS_H );

    const status = new UITextElement(
      this.ctx,
      status_x,
      status_y,
      status_w,
      status_h,
      {
        localization_key: data.status_key!,
        font_size: LayoutScaler.textSizeFromDesign( STATUS_FONT ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.friends_card_status,
        text_align: is_reminder ? MyTextAlign.Left : MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        scale_to_width: true,
        auto_wrap: false,
      }
    );

    status.setClickable( false );
    card.addChild( status );
  }

  private createRemindBadge( card: UIElement, reminder: FriendReminderDto ): void
  {
    const size = LayoutScaler.heightFromDesign( REMIND_BADGE_SIZE );
    const hit_size = LayoutScaler.heightFromDesign( REMIND_BADGE_HIT_SIZE );
    const hit_x = LayoutScaler.xFromDesign( REMIND_BADGE_X ) + ( size - hit_size ) / 2;
    const hit_y = LayoutScaler.yFromDesign( REMIND_BADGE_Y ) + ( size - hit_size ) / 2;
    const badge_offset = ( hit_size - size ) / 2;

    const hit_area = new UIElementHolder( this.ctx, hit_x, hit_y, hit_size, hit_size, false, false );
    hit_area.setOnClick( () => void this.dismissReminder( reminder ) );

    const badge = new UIImage( this.ctx, badge_offset, badge_offset, size, size, ImgID.UI_SettingsDialogCloseBtn );
    badge.setClickable( false );
    hit_area.addChild( badge, true );
    card.addChild( hit_area );
  }

  private async dismissReminder( reminder: FriendReminderDto ): Promise<void>
  {
    this.last_poll_at = Date.now();
    this.reminders = this.reminders.filter( r => r.friend.id !== reminder.friend.id );
    this.saveFriendsToCache();
    this.rebuildContents();

    try
    {
      await dismissReminder( reminder.friend.id );
    }
    catch ( e )
    {
      console.warn( "[FriendsScreen] Failed to dismiss a reminder", e );
    }

    this.last_poll_at = Date.now();
  }

  private createRemindButton( card: UIElement, card_w: number, reminder: FriendReminderDto ): void
  {
    const button_w = LayoutScaler.widthFromDesign( 348 );
    const button_h = LayoutScaler.heightFromDesign( 140 );
    const button_x = card_w - button_w - LayoutScaler.xFromDesign( CARD_ACTION_RIGHT_GAP );

    const button = new UIImage( this.ctx, button_x, 0, button_w, button_h, ImgID.UI_BlueBtn, {}, true );

    const button_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        localization_key: "FRIENDS.REMIND",
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.friends_remind_outline,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    button.addChild( button_text, true );
    card.addChild( button );

    let is_reminding = false;
    button.setOnClick( async () =>
    {
      if ( is_reminding )
        return;

      is_reminding = true;
      await this.remind( reminder );
      is_reminding = false;
    } );
  }

  private async remind( reminder: FriendReminderDto ): Promise<void>
  {
    const link = await InviteService.gameLink();
    if ( !link )
    {
      console.warn( "[FriendsScreen] Game link is not available for a reminder" );
      return;
    }

    const text = LocalizationManager.tWithParams( "FRIENDS.REMIND_MESSAGE", [ link ] );

    if ( reminder.telegram_username )
      TelegramAPISettings.openChat( reminder.telegram_username, text );
    else
      TelegramAPISettings.shareText( text );

    try
    {
      await remindFriend( reminder.friend.id );
    }
    catch ( e )
    {
      console.warn( "[FriendsScreen] Failed to mark a reminder", e );
    }

    if ( this.is_destroyed )
      return;

    this.reminders = this.reminders.filter( r => r.friend.id !== reminder.friend.id );
    this.saveFriendsToCache();
    this.rebuildContents();
  }

  private createCardButton( card: UIElement, card_w: number, tab_index: number, data: FriendCardData, reward_icons: GiftRewardIcons ): void
  {
    const is_gift_tab = tab_index === 0;

    const button_w = LayoutScaler.widthFromDesign( 348 );
    const button_h = LayoutScaler.heightFromDesign( 140 );

    const button_x = card_w - button_w - LayoutScaler.xFromDesign( CARD_ACTION_RIGHT_GAP );

    const button = new UIImage(
      this.ctx,
      button_x,
      0,
      button_w,
      button_h,
      is_gift_tab ? ImgID.UI_OrangeBtn : ImgID.UI_GreenFrameBtn,
      {},
      true
    );

    const button_text = new UITextElement(
      this.ctx,
      0,
      0,
      button_w,
      button_h,
      {
        localization_key: is_gift_tab ? "FRIENDS.GIFT" : "FRIENDS.RECEIVE",
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: is_gift_tab ? ColorScheme.hearts_text : ColorScheme.menu_button_outline,
        ...TextStyleScheme.buttonShadow(),
      }
    );

    button.addChild( button_text, true );
    card.addChild( button );

    if ( !is_gift_tab )
    {
      let is_claiming = false;
      button.setOnClick( async () =>
      {
        if ( is_claiming || !data.gift_id )
          return;

        is_claiming = true;
        await this.claimGift( data.gift_id, reward_icons );
        is_claiming = false;
      } );
      return;
    }

    let is_sending = false;
    button.setOnClick( async () =>
    {
      if ( is_sending )
        return;

      is_sending = true;
      const next_send_at = await this.sendHeartTo( data.profile.id );
      is_sending = false;
      if ( next_send_at === null || this.is_destroyed )
        return;

      card.removeChild( button );
      this.createSentBlock( card, card_w, next_send_at );
      this.forceMakeShouldBeRendered();
    } );
  }

  private async claimGift( gift_id: string, reward_icons: GiftRewardIcons ): Promise<void>
  {
    const stats_before = StatisticsStorage.load();
    let claimed: ClaimHeartGiftsDto;

    try
    {
      claimed = await claimHeartGifts( [ gift_id ] );
      if ( claimed.player )
        adoptEconomyFromPlayer( claimed.player );
    }
    catch ( e )
    {
      console.warn( "[FriendsScreen] Failed to claim a gift", e );
      return;
    }

    if ( this.is_destroyed )
      return;

    const sources = this.giftFlySources( claimed, reward_icons );

    for ( const reply of claimed.replies ?? [] )
      this.markHeartSent( reply.friend_id, reply.next_send_at );

    this.gifts = this.gifts.filter( gift => gift.id !== gift_id );
    this.syncReceiveBadge();
    this.saveFriendsToCache();
    this.rebuildContents();

    RewardFlyToHud.play( this.ctx, this, sources, stats_before );

    FriendsCache.invalidate();
    void this.loadFriends();
  }

  private giftFlySources( claimed: ClaimHeartGiftsDto, reward_icons: GiftRewardIcons ): RewardFlySource[]
  {
    const sources: RewardFlySource[] = [];

    if ( claimed.coins > 0 )
      sources.push( {
        target: "coin",
        img_id: ImgID.UI_ShopBlockImg,
        center: this.absoluteCenter( reward_icons.coin ),
        size: reward_icons.coin.getRect().getW(),
        amount: claimed.coins,
      } );

    if ( claimed.hearts > 0 )
      sources.push( {
        target: "heart",
        img_id: ImgID.IconHeart,
        center: this.absoluteCenter( reward_icons.heart ),
        size: reward_icons.heart.getRect().getW(),
        copies: Math.min( claimed.hearts, GIFT_FLY_MAX_HEARTS ),
      } );

    return sources;
  }

  private absoluteCenter( el: UIElement ): Vector2
  {
    const pos = el.getAbsolutePosition();
    const rect = el.getRect();
    return new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 );
  }

  private async sendHeartTo( friend_id: number ): Promise<number | null>
  {
    try
    {
      const result = await sendHeart( friend_id );
      this.markHeartSent( friend_id, result.next_send_at );
      return Date.parse( result.next_send_at );
    }
    catch ( e )
    {
      const body = e instanceof ApiError ? e.body as { error?: string; next_send_at?: string } | undefined : undefined;
      if ( body?.error === "gift_cooldown" && body.next_send_at )
      {
        this.markHeartSent( friend_id, body.next_send_at );
        return Date.parse( body.next_send_at );
      }

      console.warn( "[FriendsScreen] Failed to send a heart", e );
      return null;
    }
  }

  private markHeartSent( friend_id: number, next_send_at: string ): void
  {
    const friend = this.friends.find( f => f.id === friend_id );
    if ( !friend )
      return;

    friend.can_send_heart = false;
    friend.next_send_at = next_send_at;
    this.saveFriendsToCache();
  }

  private createSentBlock( card: UIElement, card_w: number, until: number ): void
  {
    const block_w = LayoutScaler.widthFromDesign( 348 );
    const block_x = card_w - block_w - LayoutScaler.xFromDesign( CARD_ACTION_RIGHT_GAP );

    const sent_text = new UITextElement(
      this.ctx,
      block_x,
      LayoutScaler.yFromDesign( 5 ),
      block_w,
      LayoutScaler.heightFromDesign( 60 ),
      {
        localization_key: "FRIENDS.SENT",
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.hearts_text,
        auto_wrap: false,
      }
    );

    sent_text.setClickable( false );
    card.addChild( sent_text );

    const pill_w = LayoutScaler.widthFromDesign( 300 );
    const pill_h = LayoutScaler.heightFromDesign( 70 );

    const pill = new RoundedRectangle(
      this.ctx,
      block_x + ( block_w - pill_w ) / 2,
      LayoutScaler.yFromDesign( 63 ),
      pill_w,
      pill_h,
      LayoutScaler.cornerRadiusFromDesign( 35 ),
      ColorScheme.hearts_timer_bg
    );

    pill.setClickable( false );
    card.addChild( pill );

    const clock_size = LayoutScaler.heightFromDesign( 66 );

    const clock = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 4 ),
      ( pill_h - clock_size ) / 2,
      clock_size,
      clock_size,
      ImgID.UI_ClockFrame
    );

    clock.setClickable( false );
    pill.addChild( clock );

    const shown = formatShortDuration( until - Date.now() );

    const timer_text = new UITextElement(
      this.ctx,
      clock_size,
      0,
      pill_w - clock_size,
      pill_h,
      {
        text: shown,
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.hearts_text,
        auto_wrap: false,
      }
    );

    timer_text.setClickable( false );
    pill.addChild( timer_text );

    this.gift_timers.push( { text: timer_text, until, shown } );
  }

  private updateGiftTimers(): void
  {
    const now_ms = Date.now();

    for ( const timer of this.gift_timers )
    {
      const text = formatShortDuration( timer.until - now_ms );

      if ( text === timer.shown )
        continue;

      timer.shown = text;
      timer.text.setText( text );
    }
  }

  private createCardCounter( card: UIElement, design_center_x: number, design_icon_size: number, icon_id: ImgID, amount: string, is_heart: boolean ): UIImage
  {
    const icon_size = LayoutScaler.heightFromDesign( design_icon_size );
    const center_x = LayoutScaler.xFromDesign( design_center_x );
    const icon_y = LayoutScaler.yFromDesign( 10 );

    const icon = new UIImage(
      this.ctx,
      center_x - icon_size / 2,
      icon_y,
      icon_size,
      icon_size,
      icon_id
    );

    icon.setClickable( false );
    card.addChild( icon );

    const text_w = LayoutScaler.widthFromDesign( 200 );
    const text_h = LayoutScaler.heightFromDesign( 50 );
    const text_lift = LayoutScaler.yFromDesign( is_heart ? 20 : 10 );

    const text = new UITextElement(
      this.ctx,
      center_x - text_w / 2,
      icon_y + icon_size - text_h / 2 - text_lift,
      text_w,
      text_h,
      {
        text: amount,
        font_size: LayoutScaler.textSizeFromDesign( 40 ),
        font_weight: 900,
        text_style: TextStyleScheme.rubik,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: ColorScheme.black,
        auto_wrap: false,
      }
    );

    text.setClickable( false );
    card.addChild( text );

    return icon;
  }

  private inviteBlockTop(): number
  {
    return this.rect.getH()
      - LayoutScaler.heightFromDesign( NAV_BAR_H )
      - LayoutScaler.heightFromDesign( INVITE_H );
  }

  private createInviteBlock(): void
  {
    const block_w = LayoutScaler.widthFromDesign( INVITE_W );
    const block_x = ( this.rect.getW() - block_w ) / 2;
    const block_y = this.inviteBlockTop();

    const block = new InviteFriendBlock( this.ctx, block_x, block_y, () => void InviteService.share() );
    this.addChild( block );
    this.invite_block = block;
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { if ( this.isBlockedByModal() ) return; super.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { if ( this.isBlockedByModal() ) return; super.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { if ( this.isBlockedByModal() ) return; super.onTouchMove( p, performance.now() ); },
      true
    );
  }

  private isBlockedByModal(): boolean
  {
    return DialogManager.isAnyDialogOpened() || OverlayManager.isInteractionBlocked();
  }

  public destroy(): void
  {
    this.is_destroyed = true;
    document.removeEventListener( "visibilitychange", this.on_visibility_change );

    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
  }

  public update( now: number ): void
  {
    this.updateBasic( now );
    this.updateGiftTimers();
    this.pollFriends();
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() )
      return;

    this.startRender();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( this.ctx );
  }

  public name(): string
  {
    return "FriendsScreen";
  }
}
